// The battle screen: a side view like the SNES Final Fantasy games. Creatures on the left, the party on the
// right, blue menu windows below with everyone's health and time bar. When your bar fills, your menu opens and
// the battle waits for you. Everything you see is what the server decided; this only plays it out.
import { ATB_FULL, CHOOSE_TICKS, formationDef, type BattleEvent, type BattleView, type Fighter } from '../../../shared/battle.js';
import { abilitiesFor, abilityDef, jobDef, STATUS, type AbilityDef, type Element } from '../../../shared/jobs.js';
import { MOBS } from '../../../shared/mobs.js';
import { itemDef } from '../../../shared/items.js';
import { drawMob, mobTop } from '../render/mobs.js';
import { drawFx, drawStatusMarks, flashOf, FX_MS, type SpellFx } from '../render/spellfx.js';
import type { Sfx } from '../audio/audio.js';
import type { Look } from '../../../shared/look.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const W = 224, H = 132;
type Mode = 'i' | 'w' | 'a';
export interface BattleHost {
  me(): number;
  look(id: number): Look | null;
  sprite(key: number, look: Look, dir: string, mode: Mode, fi: number): HTMLCanvasElement | null;
  /** The zone's ground and a dry spot near the fight to paint the battlefield from. */
  backdrop(x: number, y: number): { cv: HTMLCanvasElement; x: number; y: number } | null;
  night(): number;                 // 0 day .. 1 night
  /** Battle items in your bag (potions, ethers) and how many. */
  items(): { def: string; qty: number }[];
  send(cmd: string, target?: number, skill?: string): void;
  sound(name: Sfx, pitch?: number): void;
  /** The world as it is on screen right now, and where you are on it (canvas pixels), for the intro. */
  snapshot(): { cv: HTMLCanvasElement; x: number; y: number } | null;
}
const INTRO_MS = 700, INTRO_FADE_MS = 380;

interface Fx { t0: number; dur: number; kind: 'lunge' | 'flash' | 'num' | 'fade' | 'spell'; who: number; to?: number; text?: string; color?: string; big?: boolean; small?: boolean; fx?: SpellFx; seed?: number }
/** What each job calls its ability menu. */
const MENU_NAME: Record<string, string> = { knight: 'Skills', wizard: 'Black Magic', cleric: 'White Magic', rogue: 'Tricks' };
const EL_COLOR: Record<Element, string> = { fire: '#ffb070', ice: '#bfe9ff', thunder: '#ffe97a', light: '#fffbe0', dark: '#c9a0ff' };
const EL_SOUND: Record<Element, Sfx> = { fire: 'fire', ice: 'ice', thunder: 'thunder', light: 'holy', dark: 'debuff' };
const STATUS_FX: Partial<Record<string, SpellFx>> = { sleep: 'sleep', poison: 'poison', protect: 'protect', regen: 'regen', haste: 'haste', slow: 'ice', provoke: 'shout', sentinel: 'shield', blind: 'smoke', stun: 'bash' };
/** The effect for an ability or a creature's move. */
function fxOf(skill: string | undefined): SpellFx | null {
  if (!skill) return null;
  const a = abilityDef(skill); if (a) return a.fx;
  for (const m of Object.values(MOBS)) { const mv = m.moves?.find(x => x.id === skill); if (mv) return mv.fx; }
  return null;
}

export class BattleScreen {
  active = false;
  private view = $<HTMLCanvasElement>('battleView'); private ctx = this.view.getContext('2d')!;
  private b: BattleView | null = null;
  private queue: BattleEvent[] = []; private playing = 0;
  private fx: Fx[] = [];
  private banner = ''; private bannerUntil = 0;
  private menu: 'main' | 'skill' | 'item' | 'target-foe' | 'target-ally' | 'target-fallen' | null = null;
  private lastUse: AbilityDef | null = null;
  private pending: { cmd: string; skill?: string } | null = null;
  private turnSince = 0; private myTurn = false;
  private down = new Map<number, number>();    // fighter -> when they fell (for the fade)
  private ending: { result: string; text: string; at: number } | null = null;
  onClose: () => void = () => {};

  constructor(private host: BattleHost) {
    this.view.width = W; this.view.height = H;
    this.view.addEventListener('click', e => this.clickAt(e));
    $('bHelp').addEventListener('click', () => this.host.send('help'));
    $('bCancelRun').addEventListener('click', () => this.host.send('cancel_run'));
    $('bLeave').addEventListener('click', () => this.host.send('leave'));
    $('bResult').addEventListener('click', () => this.close());
    addEventListener('keydown', e => {
      if (!this.active) return;
      if (this.ending && this.resultShown && (e.code === 'Space' || e.code === 'Enter' || e.code === 'Escape')) { e.preventDefault(); this.close(); return; }
      if (e.code === 'Escape' && this.menu && this.menu !== 'main') { e.preventDefault(); this.openMenu('main'); return; }
      const n = Number(e.key);
      if (n >= 1 && n <= 9) { const btns = [...$('bMenuList').querySelectorAll<HTMLButtonElement>('button:not([disabled])')]; if (btns[n - 1]) { e.preventDefault(); btns[n - 1].click(); } }
      if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'KeyW', 'KeyA', 'KeyS', 'KeyD'].includes(e.code)) {
        const btns = [...$('bMenuList').querySelectorAll<HTMLButtonElement>('button:not([disabled])')]; if (!btns.length) return;
        e.preventDefault(); e.stopPropagation();
        const i = btns.indexOf(document.activeElement as HTMLButtonElement), d = ['ArrowDown', 'ArrowRight', 'KeyS', 'KeyD'].includes(e.code) ? 1 : -1;
        btns[(i + d + btns.length) % btns.length].focus();
      }
    }, true);
  }

  /** New state from the server, with what just happened. */
  update(m: { battle: BattleView; events: BattleEvent[]; start?: boolean }): void {
    if (!this.active) this.open();
    this.b = m.battle;
    this.queue.push(...m.events);
    for (const f of m.battle.fighters) if (f.state !== 'down') this.down.delete(f.id);
    const mine = m.battle.turn === this.host.me();
    if (mine && !this.myTurn) { this.turnSince = performance.now(); this.openMenu('main'); this.host.sound('open'); }
    if (!mine && this.myTurn) this.openMenu(null);
    this.myTurn = mine;
    if (!this.menu) this.idleMenu();
    this.panels();
  }

  end(m: { result: string; text: string }): void {
    this.ending = { result: m.result, text: m.text, at: performance.now() };
    this.openMenu(null);
    if (m.result === 'left') { this.close(); return; }
    this.resultShown = false;      // shown once the last action has played out (frame() calls showResult)
    this.idleMenu();
  }
  private resultShown = false;
  private showResult(): void {
    const m = this.ending!; this.resultShown = true;
    const r = $('bResult'); r.hidden = false;
    r.innerHTML = '';
    const t = document.createElement('b'); t.textContent = m.text; r.append(t);
    const s = document.createElement('small'); s.textContent = m.result === 'win' ? 'Tap or press Space to carry on' : m.result === 'fled' ? 'Safe for now' : 'You wake up by the wayshrine…'; r.append(s);
    this.host.sound(m.result === 'win' ? 'level' : m.result === 'fled' ? 'dodge' : 'close');
    setTimeout(() => { if (this.ending) this.close(); }, m.result === 'win' ? 4500 : 2500);
  }

  private open(): void {
    this.active = true; this.ending = null; this.queue = []; this.fx = []; this.down.clear(); this.banner = '';
    $('bResult').hidden = true; document.body.classList.add('in-battle');
    // the SNES moment: the world pixelates and swirls in on you, flashes, and the battle is there
    const snap = this.host.snapshot();
    if (snap) { this.intro = { ...snap, t0: performance.now() }; const fx = $<HTMLCanvasElement>('battleFx'); fx.width = snap.cv.width; fx.height = snap.cv.height; fx.hidden = false; $('battle').hidden = true; }
    else $('battle').hidden = false;
    this.host.sound('battle');
  }
  private intro: { cv: HTMLCanvasElement; x: number; y: number; t0: number } | null = null;
  private mosaic: HTMLCanvasElement = document.createElement('canvas');
  private drawIntro(now: number): void {
    const it = this.intro!, fx = $<HTMLCanvasElement>('battleFx'), c = fx.getContext('2d')!, W2 = fx.width, H2 = fx.height;
    const t = Math.max(0, now - it.t0);   // the first frame can be stamped a moment before the battle opened
    if (t >= INTRO_MS && $('battle').hidden) $('battle').hidden = false;          // the battle is underneath now
    if (t >= INTRO_MS + INTRO_FADE_MS) { fx.hidden = true; this.intro = null; return; }
    if (t < INTRO_MS) {
      const k = t / INTRO_MS, block = Math.max(1, Math.round(1 + Math.pow(k, 1.6) * Math.min(W2, H2) / 14));
      const m = this.mosaic, mw = Math.max(1, Math.ceil(W2 / block)), mh = Math.max(1, Math.ceil(H2 / block));
      if (m.width !== mw || m.height !== mh) { m.width = mw; m.height = mh; }
      const g = m.getContext('2d')!; g.imageSmoothingEnabled = true;
      g.setTransform(1, 0, 0, 1, 0, 0); g.fillStyle = '#000'; g.fillRect(0, 0, mw, mh);
      // zoom and swirl in around you
      const zoom = 1 + k * k * 1.4, turn = k * k * 1.1;
      g.translate(it.x / block, it.y / block); g.rotate(turn); g.scale(zoom, zoom); g.translate(-it.x / block, -it.y / block);
      g.drawImage(it.cv, 0, 0, W2, H2, 0, 0, W2 / block, H2 / block);
      c.imageSmoothingEnabled = false; c.setTransform(1, 0, 0, 1, 0, 0);
      c.drawImage(m, 0, 0, mw, mh, 0, 0, mw * block, mh * block);
      if (k > 0.55) { c.fillStyle = `rgba(255,255,255,${Math.min(1, (k - 0.55) / 0.45)})`; c.fillRect(0, 0, W2, H2); }
    } else {
      c.clearRect(0, 0, W2, H2);
      c.fillStyle = `rgba(255,255,255,${1 - (t - INTRO_MS) / INTRO_FADE_MS})`; c.fillRect(0, 0, W2, H2);
    }
  }
  close(): void {
    if (!this.active) return;
    this.intro = null; $('battleFx').hidden = true;
    this.active = false; this.b = null; this.ending = null; this.myTurn = false; this.menu = null;
    $('battle').hidden = true; document.body.classList.remove('in-battle');
    this.onClose();
  }

  // ---------- menus: a command window like the SNES games, with a help line for whatever is under the cursor ----------
  private openMenu(which: BattleScreen['menu']): void {
    this.menu = which;
    const box = $('bMenuList'), title = $('bMenuTitle'), help = $('bHelpLine'); box.replaceChildren();
    $('bMenu').classList.toggle('active', !!which);
    if (!which || !this.b) { this.idleMenu(); return; }
    const helpFor = (t: string) => { help.textContent = t; };
    const btn = (label: string, on: () => void, disabled = false, hint = '', right = '') => {
      const b = document.createElement('button'); b.type = 'button'; b.disabled = disabled; b.dataset.help = hint;
      const l = document.createElement('span'); l.textContent = label; b.append(l);
      if (right) { const r = document.createElement('small'); r.textContent = right; b.append(r); }
      b.addEventListener('click', on);
      const show = () => helpFor(hint || '↑↓ choose · Enter OK · Esc back');
      b.addEventListener('focus', show); b.addEventListener('mouseenter', show);
      box.append(b); return b;
    };
    const foes = this.b.fighters.filter(f => f.side === 'foe' && f.hp > 0), allies = this.b.fighters.filter(f => f.side === 'party' && f.state !== 'down');
    const fallen = this.b.fighters.filter(f => f.side === 'party' && f.state === 'down');
    const me = this.b.fighters.find(f => f.id === this.host.me());
    const pick = (cmd: string, skill?: string, who: 'foe' | 'ally' | 'fallen' = 'foe') => {
      const list = who === 'foe' ? foes : who === 'ally' ? allies : fallen;
      if (who !== 'ally' && list.length === 1) return this.choose(cmd, list[0].id, skill);
      this.pending = { cmd, skill }; this.openMenu(who === 'foe' ? 'target-foe' : who === 'ally' ? 'target-ally' : 'target-fallen');
    };
    const items = this.host.items(), nItems = items.reduce((a, i) => a + i.qty, 0);
    const skillMenu = MENU_NAME[me?.job ?? 'knight'] ?? 'Skills';
    let first: HTMLButtonElement | null = null;
    if (which === 'main') {
      title.textContent = 'Your turn';
      first = btn('Attack', () => pick('attack'), false, 'Hit a creature with your weapon. Tip: click a creature to attack it.');
      btn(skillMenu, () => this.openMenu('skill'), false, `Your ${jobDef(me?.job).name} abilities${me?.maxMp ? ` · MP ${me.mp}/${me.maxMp}` : ''}.`);
      btn('Item', () => this.openMenu('item'), nItems <= 0, nItems ? 'Potions and ethers from your bag.' : 'Nothing to use. Potions and ethers show up here.', String(nItems));
      btn('Defend', () => this.choose('defend'), false, 'Take half damage until your next turn, and get a little MP back.');
      btn(me?.helper ? 'Leave' : 'Run', () => me?.helper ? this.host.send('leave') : this.choose('run'), false, me?.helper ? 'Helpers can leave whenever they like.' : 'Everyone in your party has to choose Run for it to work.');
    } else if (which === 'skill') {
      title.textContent = `${skillMenu} · MP ${me?.mp ?? 0}`;
      const job = me?.job ?? 'knight', mp = me?.mp ?? 0;
      for (const a of abilitiesFor(job, me?.lv ?? 1)) {
        const cd = me?.cd?.[a.id] ?? 0, noTarget = a.target === 'fallen' && !fallen.length;
        const why = cd ? `Ready in ${cd} turn${cd > 1 ? 's' : ''}.` : mp < a.mp ? 'Not enough MP.' : noTarget ? 'Nobody has fainted.' : '';
        const b = btn(a.name, () => {
          if (a.target === 'foe') pick('skill', a.id, 'foe'); else if (a.target === 'ally') pick('skill', a.id, 'ally'); else if (a.target === 'fallen') pick('skill', a.id, 'fallen'); else this.choose('skill', undefined, a.id);
        }, !!why, `${a.desc}${why ? '  ' + why : ''}`, cd ? `⏳${cd}` : a.mp ? `${a.mp} MP` : '');
        b.classList.add('ab-' + a.kind); first ??= b.disabled ? null : b;
      }
      btn('Back', () => this.openMenu('main'), false, 'Back to the commands.');
    } else if (which === 'item') {
      title.textContent = 'Items';
      for (const it of items) {
        const d = itemDef(it.def);
        const b = btn(d?.name ?? it.def, () => pick('item', it.def, 'ally'), it.qty <= 0, d?.desc ?? '', `×${it.qty}`); first ??= b;
      }
      btn('Back', () => this.openMenu('main'), false, 'Back to the commands.');
    } else {
      const list = which === 'target-foe' ? foes : which === 'target-ally' ? allies : fallen;
      title.textContent = which === 'target-foe' ? 'Which creature?' : which === 'target-ally' ? 'Who?' : 'Bring back who?';
      for (const f of list) { const b = btn(f.name, () => this.choose(this.pending!.cmd, f.id, this.pending!.skill), false, `${f.name}: ${f.hp}/${f.max} health. You can also click them.`, `${f.hp}/${f.max}`); b.addEventListener('focus', () => { this.aim = f.id; }); b.addEventListener('mouseenter', () => { this.aim = f.id; }); first ??= b; }
      btn('Back', () => this.openMenu('main'), false, 'Back to the commands.');
    }
    const lead = first ?? box.firstElementChild as HTMLButtonElement | null;
    lead?.focus(); helpFor(lead?.dataset.help || '↑↓ choose · Enter OK · Esc back');
  }
  /** The command window when it isn't your turn: who's acting, and the keys. */
  private idleMenu(): void {
    const b = this.b, title = $('bMenuTitle'), help = $('bHelpLine');
    const turn = b?.turn != null ? b.fighters.find(f => f.id === b.turn) : null;
    title.textContent = this.ending ? this.ending.text : turn ? `${turn.name} is choosing…` : 'Waiting for your turn…';
    help.textContent = this.ending ? '' : 'Your menu opens when your time bar is full. Time waits while anyone chooses.';
  }
  /** The fighter under the target cursor, while picking. */
  private aim: number | null = null;

  /** Clicks on the battle picture: a creature (attack it, or pick it as the target) or a friend (pick them). */
  private clickAt(ev: MouseEvent): void {
    if (!this.b || !this.myTurn || !this.menu) return;
    const r = this.view.getBoundingClientRect(), x = (ev.clientX - r.left) / r.width * W, y = (ev.clientY - r.top) / r.height * H;
    let best: Fighter | null = null, bd = 18;
    for (const sp of this.spots().values()) {
      const top = sp.f.side === 'foe' ? mobTop(sp.f.mob ?? 'puff') * 2 : 30, d = Math.hypot(sp.x - x, sp.y - top / 2 - y);
      if (d < bd) { bd = d; best = sp.f; }
    }
    if (!best) return;
    if (best.side === 'foe' && best.hp > 0) {
      if (this.menu === 'target-foe') this.choose(this.pending!.cmd, best.id, this.pending!.skill);
      else if (this.menu === 'main') this.choose('attack', best.id);
    } else if (best.side === 'party') {
      if (this.menu === 'target-ally' && best.state !== 'down') this.choose(this.pending!.cmd, best.id, this.pending!.skill);
      if (this.menu === 'target-fallen' && best.state === 'down') this.choose(this.pending!.cmd, best.id, this.pending!.skill);
    }
  }
  private choose(cmd: string, target?: number, skill?: string): void { this.host.send(cmd, target, skill); this.aim = null; this.openMenu(null); this.myTurn = false; }

  private panels(): void {
    const b = this.b; if (!b) return;
    const row = (f: Fighter) => {
      const d = document.createElement('div'); d.className = 'b-row' + (f.state === 'down' ? ' down' : '') + (b.turn === f.id ? ' turn' : '') + (f.id === this.host.me() ? ' me' : '');
      const n = document.createElement('span'); n.className = 'b-name'; n.textContent = (f.side === 'foe' ? '' : f.state === 'run' ? '🏃 ' : f.state === 'defend' ? '🛡 ' : '') + f.name + (f.helper ? ' (helping)' : '') + (f.away ? ' (away)' : '');
      if (f.job) { const j = document.createElement('span'); j.className = 'b-job'; j.style.background = jobDef(f.job).color; j.textContent = jobDef(f.job).name.slice(0, 2) + f.lv; j.title = `${jobDef(f.job).name} level ${f.lv}`; n.prepend(j); }
      d.append(n);
      if (f.st?.length) { const st = document.createElement('span'); st.className = 'b-st'; st.textContent = f.st.map(([id]) => STATUS[id].icon).join(''); st.title = f.st.map(([id, t]) => `${STATUS[id].name} (${t})`).join(', '); n.append(st); }
      if (f.side === 'party') {
        const hp = document.createElement('span'); hp.className = 'b-hp' + (f.hp / f.max <= 0.25 ? ' low' : ''); hp.textContent = `${f.hp}/${f.max}`;
        if (f.maxMp) { const mp = document.createElement('small'); mp.className = 'b-mp'; mp.textContent = `${f.mp}`; mp.title = `MP ${f.mp}/${f.maxMp}`; hp.append(mp); }
        const bar = document.createElement('span'); bar.className = 'b-atb' + (f.atb >= ATB_FULL ? ' full' : ''); const fill = document.createElement('i'); fill.style.width = `${Math.round(f.atb / ATB_FULL * 100)}%`; bar.append(fill);
        d.append(hp, bar);
      } else { const lv = document.createElement('span'); lv.className = 'b-lv'; lv.textContent = `Lv${f.lv}`; d.append(lv); }
      return d;
    };
    $('bFoes').replaceChildren(...b.fighters.filter(f => f.side === 'foe' && f.hp > 0).map(row));
    $('bParty').replaceChildren(...b.fighters.filter(f => f.side === 'party').map(row));
    const fd = formationDef(b.formation); $('bForm').textContent = `${fd.icon}  ${fd.name}${b.chain >= 2 ? `  ·  chain ×${b.chain}` : ''}`; $('bForm').title = fd.perks.join(' · ');
    const me = b.fighters.find(f => f.id === this.host.me());
    $('bHelp').hidden = !!me?.helper; $('bHelp').textContent = b.help ? 'Help is on: strangers can join' : 'Call for help';
    $('bHelp').classList.toggle('on', b.help);
    $('bLeave').hidden = !me?.helper;
    $('bCancelRun').hidden = me?.state !== 'run';
    const run = $('bRun');
    run.hidden = b.runners === 0;
    run.textContent = b.runners < b.needRun ? `Ready to run (${b.runners}/${b.needRun}): everyone has to choose Run` : `Everyone's running! ${Math.round(b.runOdds * 100)}% chance to get away`;
  }

  // ---------- playing it out ----------
  private step(now: number): void {
    if (now < this.playing || !this.queue.length) return;
    const e = this.queue.shift()!;
    const backlog = this.queue.length;
    const say = (t: string, ms = 1300) => { this.banner = t; this.bannerUntil = now + ms; };
    const name = (id: number) => this.b?.fighters.find(f => f.id === id)?.name ?? '';
    const spell = (who: number, fx: SpellFx, at = now) => this.fx.push({ t0: at, dur: FX_MS[fx], kind: 'spell', who, fx, seed: Math.random() * 1000 });
    const num = (who: number, text: string, color: string, at = now, big = false) => this.fx.push({ t0: at, dur: big ? 1200 : 900, kind: 'num', who, text, color, big });
    switch (e.k) {
      case 'use': {
        const a = abilityDef(e.skill); this.lastUse = a ?? null;
        say(`${name(e.from)}: ${e.name}`, 1000);
        if (a?.kind === 'spell') { spell(e.from, 'steal'); this.host.sound('cast'); }
        if (a && !a.power && !a.heal && !a.revive && !a.steal) {         // a status-only ability: play it now on its targets
          for (const t of e.to) spell(t, a.fx, now + 120);
          this.host.sound(a.status && STATUS[a.status.id].good ? 'buff' : 'debuff');
        }
        if (!a) { const fx = fxOf(e.skill); if (fx === 'roar') for (const t of e.to) spell(t, fx); }
        this.playing = now + (a?.kind === 'spell' ? 380 : 220); break;
      }
      case 'hit': {
        const fx = fxOf(e.skill), a = abilityDef(e.skill), isSpell = a?.kind === 'spell';
        const land = isSpell && fx ? Math.round(FX_MS[fx] * 0.45) : 180;
        if (!isSpell) this.fx.push({ t0: now, dur: 360, kind: 'lunge', who: e.from, to: e.to });
        if (fx) spell(e.to, fx, isSpell ? now : now + 120);
        if (e.burst) spell(e.to, 'burst', now + land);
        this.fx.push({ t0: now + land, dur: 260, kind: 'flash', who: e.to });
        num(e.to, e.burst || e.crit ? `${e.amt}!` : String(e.amt), e.burst ? '#ffe97a' : e.el ? EL_COLOR[e.el] : e.crit ? '#ffe9a8' : '#ffffff', now + land, e.burst || e.crit);
        if (e.weak) this.fx.push({ t0: now + land + 120, dur: 1000, kind: 'num', who: e.to, text: e.weak === 'weak' ? 'WEAK' : 'resist', color: e.weak === 'weak' ? '#ffb070' : '#a3a8b0', small: true });
        const foeHit = this.b?.fighters.find(f => f.id === e.to)?.side === 'foe';
        if (e.el && isSpell) this.host.sound(EL_SOUND[e.el]);
        setTimeout(() => this.host.sound(foeHit ? 'hit' : 'hurt', e.burst ? 0.8 : 1), land);
        this.playing = now + land + (e.burst ? 900 : e.crit || a ? 520 : 340); break;
      }
      case 'burst': say(`${e.name}  +${e.bonus}%`, 1500); this.host.sound('burst'); this.chainFx = { n: 6, t0: now }; break;
      case 'status': {
        const def = STATUS[e.id];
        if (e.on) { num(e.to, def.name, def.good ? '#a3def4' : '#d9a0ff', now + 200); if (this.lastUse?.power && STATUS_FX[e.id]) spell(e.to, STATUS_FX[e.id]!, now + 150); }
        else if (e.id === 'sleep') num(e.to, 'awake!', '#ffffff');
        this.playing = now + 260; break;
      }
      case 'tick': {
        spell(e.who, e.id === 'poison' ? 'poison' : 'regen');
        num(e.who, e.id === 'poison' ? String(e.amt) : `+${e.amt}`, e.id === 'poison' ? '#d9a0ff' : '#8fe08a', now + 200);
        this.host.sound(e.id === 'poison' ? 'poison' : 'heal'); this.playing = now + 600; break;
      }
      case 'skip': say(e.why === 'sleep' ? `${name(e.who)} is fast asleep…` : `${name(e.who)} is stunned!`, 900); if (e.why === 'sleep') spell(e.who, 'sleep'); this.playing = now + 650; break;
      case 'steal': {
        const item = e.item ? itemDef(e.item)?.name ?? e.item : null;
        spell(e.to, 'steal'); say(item ? `${name(e.from)} stole ${item}!` : `${name(e.from)} came up empty.`, 1100);
        this.host.sound(item ? 'steal' : 'error'); this.playing = now + 600; break;
      }
      case 'revive': spell(e.to, 'revive'); num(e.to, `+${e.amt}`, '#8fe08a', now + 500); say(`${name(e.to)} is back on their feet!`, 1200); this.down.delete(e.to); this.host.sound('holy'); this.playing = now + 1000; break;
      case 'mp': num(e.who, `+${e.amt} MP`, '#8fc7ff'); this.playing = now + 200; break;
      case 'miss': this.fx.push({ t0: now, dur: 360, kind: 'lunge', who: e.from, to: e.to }, { t0: now + 180, dur: 800, kind: 'num', who: e.to, text: 'miss', color: '#a3def4' }); this.host.sound('dodge'); this.playing = now + 520; break;
      case 'heal': {
        spell(e.to, 'heal'); num(e.to, `+${e.amt}`, '#8fe08a', now + 300);
        if (e.item) say(`${name(e.from)} used ${itemDef(e.item)?.name ?? 'a potion'}.`, 900);
        this.host.sound(e.item ? 'loot' : 'heal'); this.playing = now + 650; break;
      }
      case 'defend': say(`${name(e.who)} braces.`, 800); this.playing = now + 350; break;
      case 'run': say(e.on ? `${name(e.who)} wants to run!` : `${name(e.who)} stays to fight.`, 1000); this.playing = now + 350; break;
      case 'flee': say(e.ok ? 'Got away!' : "Couldn't get away!", 1100); if (!e.ok) this.host.sound('error'); this.playing = now + 700; break;
      case 'down': this.down.set(e.who, now); this.fx.push({ t0: now, dur: 600, kind: 'fade', who: e.who }); if (this.b?.fighters.find(f => f.id === e.who)?.side === 'foe') this.host.sound('poof'); this.playing = now + 450; break;
      case 'join': say(`${name(e.who)} ${e.helper ? 'came to help!' : 'joined!'}`, 900); this.host.sound('join'); this.playing = now + 250; break;
      case 'leave': say(`${name(e.who)} left.`, 900); this.playing = now + 300; break;
      case 'say': say(e.text, 1400); this.playing = now + 450; break;
      case 'chain': say(`${e.name}! ${e.n}-hit chain  +${e.bonus}%`, 1100); this.host.sound('chain', 1 + (e.n - 2) * 0.12); this.chainFx = { n: e.n, t0: now }; break;
    }
    // a lot still waiting (several friends joining at once, a busy turn): hurry so the screen never lags far behind
    if (backlog > 3 && (e.k === 'say' || e.k === 'join' || e.k === 'leave' || e.k === 'status' || e.k === 'mp')) this.playing = Math.min(this.playing, now + 90);
  }

  private chainFx: { n: number; t0: number } | null = null;
  /** Where each fighter stands on the battle screen (feet, in battle-screen pixels), by formation. */
  private spots(): Map<number, { x: number; y: number; f: Fighter; face: 'left' | 'right' }> {
    const out = new Map<number, { x: number; y: number; f: Fighter; face: 'left' | 'right' }>(), b = this.b; if (!b) return out;
    const foes = b.fighters.filter(f => f.side === 'foe'), party = b.fighters.filter(f => f.side === 'party');
    const col = (list: Fighter[], x0: number, dx: number, face: 'left' | 'right', yMid = 84) => list.forEach((f, i) => {
      const step = list.length > 3 ? 10 : 15, y = yMid + i * step - (list.length - 1) * step / 2; out.set(f.id, { x: x0 + (i % 2) * dx, y, f, face });
    });
    const form = b.formation;
    if (form === 'pincer' || form === 'surround') {
      col(foes, 112, 10, 'right', 86);
      if (form === 'pincer') { col(party.filter(f => f.pos !== 'left'), 176, 10, 'left'); col(party.filter(f => f.pos === 'left'), 48, -10, 'right'); }
      else party.forEach((f, i) => { const a = (i / party.length) * Math.PI * 2; const x = 112 + Math.cos(a) * 64, y = 86 + Math.sin(a) * 24; out.set(f.id, { x, y, f, face: x > 112 ? 'left' : 'right' }); });
    } else {
      col(foes, 58, -12, 'right');
      if (form === 'wall') { col(party.filter(f => f.pos === 'front'), 154, 6, 'left'); col(party.filter(f => f.pos === 'back'), 190, 6, 'left'); }
      else col(party, 168, 12, 'left');
    }
    return out;
  }

  /** True while the intro covers the screen (the world is still drawn underneath it until then). */
  get intro_(): boolean { return !!this.intro && now_() - this.intro.t0 < INTRO_MS; }

  frame(now: number): void {
    if (!this.active) return;
    if (this.intro) this.drawIntro(now);
    if (this.intro && now - this.intro.t0 < INTRO_MS) return;
    this.step(now);
    if (this.ending && !this.resultShown && !this.queue.length && now >= this.playing + 250) this.showResult();
    const c = this.ctx; c.imageSmoothingEnabled = false;
    c.setTransform(1, 0, 0, 1, 0, 0);
    this.drawBackdrop(c, now);
    const spots = this.spots();
    const offset = (id: number): [number, number] => {
      let ox = 0, oy = 0;
      for (const f of this.fx) if (f.kind === 'lunge' && f.who === id) {
        const k = (now - f.t0) / f.dur; if (k < 0 || k > 1) continue;
        const me = spots.get(id), to = f.to != null ? spots.get(f.to) : null;
        const dir = me && to ? Math.sign(to.x - me.x) : 1, s = Math.sin(k * Math.PI);
        ox += dir * s * 16; oy += -s * 3;
      }
      return [ox, oy];
    };
    // everyone, back to front
    const order = [...spots.values()].sort((a, b) => a.y - b.y);
    for (const { x, y, f } of order) {
      const [ox, oy] = offset(f.id), downAt = this.down.get(f.id);
      let alpha = 1; if (f.side === 'foe' && downAt) alpha = Math.max(0, 1 - (now - downAt) / 600);
      if (alpha <= 0) continue;
      c.globalAlpha = alpha;
      c.fillStyle = 'rgba(30,24,30,.25)'; c.beginPath(); c.ellipse(x, y, f.side === 'foe' ? 12 : 8, 2.5, 0, 0, Math.PI * 2); c.fill();
      const flash = this.fx.some(q => q.kind === 'flash' && q.who === f.id && now >= q.t0 && now < q.t0 + q.dur && Math.floor((now - q.t0) / 60) % 2 === 0);
      if (f.side === 'foe') {
        c.save(); c.translate(Math.round(x + ox), Math.round(y + oy)); c.scale(2, 2);
        drawMob(c, { kind: f.mob ?? 'puff', x: 0, y: 0, dir: 1, anim: 0, t: 0, hp: f.hp, max: f.max, hurtAt: flash ? now : 0, deadAt: 0, id: f.id }, now);
        c.restore();
        if (b_isTarget(this, f)) this.cursor(c, x, y - mobTop(f.mob ?? 'puff') * 2 - 6, now);
      } else {
        const look = this.host.look(f.id);
        const lunging = this.fx.some(q => q.kind === 'lunge' && q.who === f.id && now >= q.t0 && now < q.t0 + q.dur);
        const mode: Mode = lunging ? 'a' : f.state === 'down' ? 'i' : 'i';
        const fi = lunging ? Math.min(4, Math.floor((now - this.fx.find(q => q.kind === 'lunge' && q.who === f.id)!.t0) / 80)) : 0;
        const face = spots.get(f.id)!.face, dir = f.state === 'run' ? (face === 'left' ? 'right' : 'left') : face;
        const s = look ? this.host.sprite(f.id, look, dir, mode, fi) : null;
        if (s) {
          if (f.state === 'down') { c.save(); c.translate(Math.round(x + ox), Math.round(y)); c.rotate(-Math.PI / 2); c.drawImage(s, -31, -16); c.restore(); }
          else { if (flash) c.globalAlpha = 0.5; c.drawImage(s, Math.round(x - 16 + ox), Math.round(y - 31 + oy)); c.globalAlpha = alpha; }
        }
        if (f.state === 'defend') { c.fillStyle = '#a3def4'; c.fillRect(Math.round(x - 12), Math.round(y - 22), 3, 6); c.fillStyle = '#2f2a26'; c.fillRect(Math.round(x - 13), Math.round(y - 23), 1, 8); }
        if (this.b?.turn === f.id) this.cursor(c, x, y - 36, now, true);
        if (b_isAllyTarget(this, f)) this.cursor(c, x, y - 36, now);
      }
      c.globalAlpha = 1;
    }
    // statuses over heads, then spell effects in front of everyone
    for (const { x, y, f } of order) {
      if (f.state === 'down') continue;
      const top = f.side === 'foe' ? mobTop(f.mob ?? 'puff') * 2 : 31;
      drawStatusMarks(c, f.st, x, y - top - 2, now);
    }
    let flash = 0;
    for (const q of this.fx) {
      if (q.kind !== 'spell' || now < q.t0) continue;
      const sp = spots.get(q.who); if (!sp) continue;
      const k = (now - q.t0) / q.dur, top = sp.f.side === 'foe' ? mobTop(sp.f.mob ?? 'puff') * 2 : 30;
      drawFx(c, q.fx!, sp.x, sp.y, top, k, q.seed ?? 0);
      flash = Math.max(flash, flashOf(q.fx!, k));
    }
    if (flash > 0) { c.fillStyle = `rgba(255,255,255,${flash})`; c.fillRect(0, 0, W, H); }
    // floating numbers
    c.font = '8px "Pixelify Sans", "Silkscreen", monospace'; c.textAlign = 'center';
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i], k = (now - f.t0) / f.dur;
      if (k > 1) { this.fx.splice(i, 1); continue; }
      if (k < 0 || f.kind !== 'num') continue;
      const sp = spots.get(f.who); if (!sp) continue;
      // the amount floats up over their head; a WEAK / resist label sits beside it, lower down
      const top = sp.f.side === 'foe' ? mobTop(sp.f.mob ?? 'puff') * 2 : 30, y = Math.round(sp.y - top - 4 - k * 10 - Math.sin(Math.min(1, k * 4) * Math.PI) * 4) + (f.small ? 12 : 0);
      c.globalAlpha = Math.min(1, (1 - k) * 3);
      if (f.big) c.font = '11px "Pixelify Sans", "Silkscreen", monospace';
      if (f.small) c.font = '6px "Silkscreen", monospace';
      c.fillStyle = '#2f2a26'; for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) c.fillText(f.text!, sp.x + dx, y + dy);
      c.fillStyle = f.color!; c.fillText(f.text!, sp.x, y);
      if (f.big || f.small) c.font = '8px "Pixelify Sans", "Silkscreen", monospace';
      c.globalAlpha = 1;
    }
    // a chain: a ring of sparkles pulses out from the creatures
    if (this.chainFx && now - this.chainFx.t0 < 700) {
      const k = (now - this.chainFx.t0) / 700, foe = [...spots.values()].find(q => q.f.side === 'foe' && q.f.hp > 0);
      if (foe) for (let i = 0; i < 10 + this.chainFx.n * 3; i++) { const a = i / (10 + this.chainFx.n * 3) * Math.PI * 2; c.fillStyle = i % 2 ? '#ffe9a8' : '#ffffff'; c.globalAlpha = 1 - k; c.fillRect(Math.round(foe.x + Math.cos(a) * (6 + k * 30)), Math.round(foe.y - 12 + Math.sin(a) * (4 + k * 16)), 2, 2); }
      c.globalAlpha = 1;
    }
    // the banner: what's happening (real text over the picture, so it stays crisp)
    const showBanner = !!this.banner && now < this.bannerUntil && !this.resultShown, bn = $('bBanner');
    if (bn.hidden === showBanner) bn.hidden = !showBanner;
    if (showBanner && bn.textContent !== this.banner) bn.textContent = this.banner;
    // whose turn, and how long they have
    if (this.myTurn && this.menu) {
      const left = Math.max(0, CHOOSE_TICKS / 60 - (now - this.turnSince) / 1000);
      $('bTimer').textContent = `Your turn · ${Math.ceil(left)}s`; $('bTimer').hidden = false;
    } else $('bTimer').hidden = true;
    c.textAlign = 'left';
  }

  private cursor(c: CanvasRenderingContext2D, x: number, y: number, now: number, me = false): void {
    const bob = Math.floor(now / 250) % 2, X = Math.round(x), Y = Math.round(y) + bob;
    c.fillStyle = '#2f2a26'; c.fillRect(X - 3, Y - 1, 7, 2); c.fillRect(X - 2, Y + 1, 5, 1); c.fillRect(X - 1, Y + 2, 3, 1);
    c.fillStyle = me ? '#ffe9a8' : '#ffffff'; c.fillRect(X - 2, Y - 1, 5, 1); c.fillRect(X - 1, Y, 3, 1); c.fillRect(X, Y + 1, 1, 1);
  }

  private bg: { key: string; cv: HTMLCanvasElement } | null = null;
  private drawBackdrop(c: CanvasRenderingContext2D, now: number): void {
    const b = this.b; if (!b) return;
    const key = `${b.zone}:${b.x}:${b.y}`;
    if (this.bg?.key !== key) {
      const cv = document.createElement('canvas'); cv.width = W; cv.height = H; const g = cv.getContext('2d')!; g.imageSmoothingEnabled = false;
      const bd = this.host.backdrop(b.x, b.y);
      // the field: the zone's own ground near the circle (a dry patch), seen at a slant
      if (bd) for (let y = 46; y < H; y++) {
        const k = (y - 46) / (H - 46), sy = Math.round(bd.y - 30 + k * 60), span = 120 + k * 60;
        g.drawImage(bd.cv, Math.max(0, Math.round(bd.x - span / 2)), Math.max(0, sy), Math.round(span), 1, 0, y, W, 1);
      } else { g.fillStyle = '#9fca6d'; g.fillRect(0, 46, W, H - 46); }
      this.bg = { key, cv };
    }
    const night = this.host.night();
    const sky = c.createLinearGradient(0, 0, 0, 50);
    sky.addColorStop(0, night > 0.5 ? '#1c2244' : '#8cc8ec'); sky.addColorStop(1, night > 0.5 ? '#3a3a6a' : '#d8eef8');
    c.fillStyle = sky; c.fillRect(0, 0, W, 50);
    // far hills and trees
    c.fillStyle = night > 0.5 ? '#2a3a3a' : '#7fae62';
    for (let x = 0; x < W; x++) { const h = 8 + Math.sin(x * 0.045) * 4 + Math.sin(x * 0.13 + 1) * 2; c.fillRect(x, 48 - h, 1, h); }
    c.fillStyle = night > 0.5 ? '#1e2c2a' : '#5e8a4a';
    for (let x = 0; x < W; x += 9) { const h = 6 + ((x * 37) % 5); c.fillRect(x, 48 - h, 7, h); c.fillRect(x + 2, 48 - h - 2, 3, 2); }
    c.drawImage(this.bg!.cv, 0, 46, W, H - 46, 0, 46, W, H - 46);
    c.fillStyle = 'rgba(255,255,255,.18)'; c.fillRect(0, 46, W, 1);
    if (night > 0) { c.fillStyle = `rgba(20,24,60,${night * 0.35})`; c.fillRect(0, 0, W, H); }
    void now;
  }

  /** Which fighters to point at while picking a target. */
  isTarget(f: Fighter): boolean { return this.menu === 'target-foe' && f.side === 'foe' && (this.aim == null || this.aim === f.id); }
  isAllyTarget(f: Fighter): boolean { return ((this.menu === 'target-ally' && f.side === 'party' && f.state !== 'down') || (this.menu === 'target-fallen' && f.side === 'party' && f.state === 'down')) && (this.aim == null || this.aim === f.id); }
}
const b_isTarget = (s: BattleScreen, f: Fighter) => s.isTarget(f);
const b_isAllyTarget = (s: BattleScreen, f: Fighter) => s.isAllyTarget(f);

const now_ = () => performance.now();
